import React from "react";
import { ARGENTARIO_COORDS } from "./EditorialLabel";

/**
 * PageHeader — the ONE header used by every public page.
 *
 *   [ LABEL ] — ISSUE N° 26 · 42°26'N · 11°08'E
 *   BIG TITLE accent.
 *   tag · tag · tag
 *   sub copy                                   [ right slot ]
 *
 * `image` switches to a photo-hero variant (same typography, sits on a photo).
 */
export default function PageHeader({
    label,
    title,
    accent,
    tagline = [],
    sub,
    right,
    image,
    imageAlt = "",
    issue = "ISSUE N° 26",
    testid = "page-header",
    className = "",
    size = "lg",
}) {
    const h1Size = size === "xl"
        ? "text-[54px] sm:text-[80px] md:text-[120px] lg:text-[150px]"
        : "text-[48px] sm:text-[64px] md:text-[96px] lg:text-[120px]";

    const body = (
        <div className={`max-w-[1400px] mx-auto px-5 md:px-10 ${image ? "pb-10 md:pb-14 pt-24" : "pt-12 md:pt-16 pb-8 md:pb-10"}`}>
            <div className="flex items-center gap-3 label-mono text-abnormal-paperDim/70 mb-5 flex-wrap">
                <span className="text-abnormal-acid">[ {label} ]</span>
                <span className="w-6 h-px bg-abnormal-acid/60" />
                <span>{issue}</span>
                <span className="hidden sm:inline w-1 h-1 rounded-full bg-abnormal-acid" />
                <span className="hidden sm:inline text-abnormal-acid">{ARGENTARIO_COORDS}</span>
            </div>

            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div className="min-w-0 poster-in">
                    <h1 className={`font-display uppercase leading-[0.85] tracking-tighter text-abnormal-paper ${h1Size}`}>
                        {title}{accent && <> <span className="text-abnormal-paperDim">{accent}</span></>}<span className="text-abnormal-acid">.</span>
                    </h1>
                    {tagline.length > 0 && (
                        <p className="mt-4 flex flex-wrap items-center gap-2 font-display uppercase text-abnormal-paper text-lg md:text-2xl leading-none tracking-tight">
                            {tagline.map((tg, i) => (
                                <React.Fragment key={i}>
                                    {i > 0 && <span className="w-1.5 h-1.5 rounded-full bg-abnormal-acid inline-block" />}
                                    <span className={i === 0 ? "text-abnormal-acid" : ""}>{tg}</span>
                                </React.Fragment>
                            ))}
                        </p>
                    )}
                    {sub && (
                        <p className="mt-5 max-w-2xl text-abnormal-paperDim text-base md:text-lg leading-relaxed">{sub}</p>
                    )}
                </div>
                {right && <div className="poster-in poster-delay-2 min-w-0 max-w-full md:flex-shrink-0">{right}</div>}
            </div>
        </div>
    );

    if (image) {
        return (
            <section data-testid={testid} className={`relative overflow-hidden border-b border-white/10 ${className}`}>
                <img src={image} alt={imageAlt} className="absolute inset-0 w-full h-full object-cover" loading="eager" decoding="async" />
                <div className="absolute inset-0 bg-black/55" />
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black to-transparent" />
                <div className="relative z-10 min-h-[52vh] flex flex-col justify-end">{body}</div>
            </section>
        );
    }

    return (
        <section data-testid={testid} className={`relative border-b border-white/10 topo-lines ${className}`}>
            {body}
        </section>
    );
}

/* Section marker used inside pages: [ N° 02 ] — TITLE ............ right */
export function SectionMark({ n, title, right, className = "" }) {
    return (
        <div className={`flex items-baseline justify-between gap-4 border-b border-white/15 pb-3 mb-6 ${className}`}>
            <div className="flex items-baseline gap-3 min-w-0">
                <span className="font-vt text-[11px] md:text-sm tracking-[0.3em] text-abnormal-acid whitespace-nowrap">[ N° {n} ]</span>
                <span className="w-4 h-px bg-abnormal-acid/60 flex-shrink-0" />
                <span className="font-vt text-[11px] md:text-sm tracking-[0.35em] uppercase text-abnormal-paper truncate">{title}</span>
            </div>
            {right && <div className="flex-shrink-0">{right}</div>}
        </div>
    );
}
